import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExperimentDetailsHeaderProps } from '../../types'
import HeaderLargeView from './headerLargeView'
import HeaderSmallView from './headerSmallView'
import Icon from '../../../../components/icons'
import Text from '../../../../elements/Typo/Text'
import { trackEvent } from '../../../../_common/functions'
import ExperimentStatusModal from '../../../../components/modal/ExperimentStatus.modal'

const ExperimentDetailsHeader: React.FC<ExperimentDetailsHeaderProps> = ({
    kpiImpactsData,
    experimentDetails,
    groupOptions,
    selectedGroup,
    setSelectedGroup,
    dropdownOptions,
    selectedKPI,
    setSelectedKPI,
    mteCardData,
    dmlCardData,
    setSelectedDateRange,
    selectedDateRange,
    setSelectedConfig,
    user,
    comments,
    openComment,
    selectedConfig,
    nMatches,
    cliper,
    MeasurementAdvanceConfig,
    getCStatus,
    cardDetails,
    cardLoading,
    analysisData,
    setIsInconclusiveModalOpen,
    setIsNoImpactModalOpen,
    toDownloadAsReport,
    onUpdate,
}) => {
    const { t } = useTranslation()
    const [headerExpend, setHeaderExpend] = useState<boolean>(true)
    const [showAlertMsg, setShowAlertMsg] = useState<boolean>(false)
    const [isStatusModalModalOpen, setIsStatusModalModalOpen] = useState<boolean>(false)

    const headerRef = useRef<HTMLDivElement>(null)
    const headerLargeRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (showAlertMsg) {
            trackEvent('experiment_details_partial_date_alert_shown', {
                experiment_literal: experimentDetails?.experiment_literal,
                start_date: experimentDetails?.start_date,
                end_date: experimentDetails?.end_date,
            })
        }
    }, [showAlertMsg])

    const _handleShowExperimentStatus = () => {
        setIsStatusModalModalOpen(true)
    }

    useEffect(() => {
        const handleScroll = () => {
            const LY: number = headerLargeRef.current?.getBoundingClientRect().top || 300
            setHeaderExpend((prev) => {
                if (prev && LY < -92) return false // collapse
                if (!prev && LY > -92) return true // expand
                return prev // no change
            })
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    return (
        <>
            <div
                ref={headerRef}
                className={`transition-all  duration-300 bg-white sticky top-[4.65rem] z-10 px-2 py-0 ${headerExpend ? 'max-h-0' : 'max-h-max  py-2 border-b border-[#E5E5E5]'}`}
                style={{ boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.05)' }}
            >
                {!headerExpend && (
                    <HeaderSmallView
                        _ref={null}
                        kpiImpactsData={kpiImpactsData}
                        experimentDetails={experimentDetails}
                        groupOptions={groupOptions}
                        selectedGroup={selectedGroup}
                        setSelectedGroup={setSelectedGroup}
                        dropdownOptions={dropdownOptions}
                        selectedKPI={selectedKPI}
                        setSelectedKPI={setSelectedKPI}
                        setSelectedDateRange={setSelectedDateRange}
                        selectedDateRange={selectedDateRange}
                        setSelectedConfig={setSelectedConfig}
                        setShowAlertMsg={setShowAlertMsg}
                        mteCardData={mteCardData}
                        dmlCardData={dmlCardData}
                        user={user}
                        comments={comments}
                        openComment={openComment}
                        selectedConfig={selectedConfig}
                        nMatches={nMatches}
                        cliper={cliper}
                        MeasurementAdvanceConfig={MeasurementAdvanceConfig}
                        getCStatus={getCStatus}
                        cardDetails={cardDetails}
                        analysisData={analysisData}
                        _handleShowExperimentStatus={_handleShowExperimentStatus}
                        setIsInconclusiveModalOpen={setIsInconclusiveModalOpen}
                        setIsNoImpactModalOpen={setIsNoImpactModalOpen}
                        toDownloadAsReport={false}
                        onUpdate={onUpdate}
                    />
                )}
            </div>

            <HeaderLargeView
                _ref={headerLargeRef}
                kpiImpactsData={kpiImpactsData}
                experimentDetails={experimentDetails}
                groupOptions={groupOptions}
                selectedGroup={selectedGroup}
                setSelectedGroup={setSelectedGroup}
                dropdownOptions={dropdownOptions}
                selectedKPI={selectedKPI}
                setSelectedKPI={setSelectedKPI}
                setSelectedDateRange={setSelectedDateRange}
                selectedDateRange={selectedDateRange}
                setSelectedConfig={setSelectedConfig}
                setShowAlertMsg={setShowAlertMsg}
                mteCardData={mteCardData}
                dmlCardData={dmlCardData}
                user={user}
                comments={comments}
                openComment={openComment}
                selectedConfig={selectedConfig}
                nMatches={nMatches}
                cliper={cliper}
                MeasurementAdvanceConfig={MeasurementAdvanceConfig}
                getCStatus={getCStatus}
                cardDetails={cardDetails}
                cardLoading={cardLoading}
                analysisData={analysisData}
                _handleShowExperimentStatus={_handleShowExperimentStatus}
                setIsInconclusiveModalOpen={setIsInconclusiveModalOpen}
                setIsNoImpactModalOpen={setIsNoImpactModalOpen}
                toDownloadAsReport={false}
                onUpdate={onUpdate}
            />

            {showAlertMsg && (
                <div className="flex bg-[#E5B6114D] rounded-[0.364rem] py-[0.364rem] px-[0.909rem] mx-[0.7rem]">
                    <Icon size={'0.8'} name="warning" className="text-[#8A7117]" />
                    <Text size="xs" className="!text-[0.636rem] !text-[#796B4B] ">
                        {t('You are viewing results for a subset of the measurement period. These results are directional and should be used with caution.')}
                    </Text>
                </div>
            )}

            {(isStatusModalModalOpen && (
                <ExperimentStatusModal
                    isModalOpen={isStatusModalModalOpen}
                    onClose={() => setIsStatusModalModalOpen(false)}
                    data={analysisData}
                    experiment_literal={experimentDetails?.experiment_literal}
                    experiment_status={experimentDetails?.status_text}
                    experimentDetails={experimentDetails}
                />
            )) || <></>}
        </>
    )
}

export default ExperimentDetailsHeader
